import { FC, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { LoadingSpinner } from 'styles/sharedStyles';
import { DsoModalProps } from './DsoModal.types';
import * as Styled from './DsoModal.styles';
import { getContentWidth } from './DsoModal.styles';

const DsoModal: FC<DsoModalProps> = ({
  isOpen = true,
  onClose,
  dismissOnOverlayClick = true,
  width,
  size,
  centered = false,
  title,
  icon,
  showCloseButton = false,
  footer,
  children,
  isLoading = false,
  loadingText,
}) => {
  const handleOverlayClick = useCallback(() => {
    if (dismissOnOverlayClick && onClose) {
      onClose();
    }
  }, [dismissOnOverlayClick, onClose]);

  const handleContentClick = useCallback(
    (e: React.MouseEvent) => e.stopPropagation(),
    []
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose();
    };

    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const contentWidth = getContentWidth(size, width);
  const hasHeader = title || icon || showCloseButton;

  const modalContent = (
    <Styled.Overlay $centered={centered} onClick={handleOverlayClick}>
      <Styled.Content
        $width={contentWidth}
        $centered={centered}
        onClick={handleContentClick}
        role="dialog"
        aria-modal="true"
      >
        {isLoading ? (
          <Styled.LoadingBody>
            <LoadingSpinner width="40px" height="40px" isPrimary={true} />
            {loadingText && <Styled.LoadingText>{loadingText}</Styled.LoadingText>}
          </Styled.LoadingBody>
        ) : (
          <>
            {hasHeader && (
              <Styled.Header>
                <Styled.HeaderLeft>
                  {icon}
                  {title && <Styled.Title>{title}</Styled.Title>}
                </Styled.HeaderLeft>
                {showCloseButton && onClose && (
                  <Styled.CloseButton onClick={onClose} aria-label="Close">
                    <MdClose size={22} />
                  </Styled.CloseButton>
                )}
              </Styled.Header>
            )}
            <Styled.Body>{children}</Styled.Body>
            {footer && <Styled.Footer>{footer}</Styled.Footer>}
          </>
        )}
      </Styled.Content>
    </Styled.Overlay>
  );

  return createPortal(modalContent, document.body);
};

export default DsoModal;
